/**
 * WelcomeModal (P2.1-S6).
 *
 * 3-step intro lần đầu user authenticated. Lightweight modal pattern
 * (theo PublishDialog/CommandPalette — no Radix Dialog dep).
 */
import { ChevronRight, MessageCircle, Search, Settings, X } from "lucide-react";
import * as React from "react";

import { BrandLogo, GradientText } from "@/components/ui/gradient";
import { Button } from "@/components/ui/button";
import { markWelcomeSeen } from "@/lib/onboarding";
import { useOnboardingStore } from "@/stores/onboarding";

interface Step {
  title: string;
  description: React.ReactNode;
  visual?: React.ReactNode;
}

export function WelcomeModal(): JSX.Element | null {
  const open = useOnboardingStore((s) => s.welcomeOpen);
  const closeWelcome = useOnboardingStore((s) => s.closeWelcome);
  const [step, setStep] = React.useState(0);

  const dismiss = (markSeen = true): void => {
    if (markSeen) markWelcomeSeen();
    closeWelcome();
    setStep(0);
  };

  const steps: Step[] = React.useMemo(
    () => [
      {
        title: "Chào mừng đến Trợ lý AI",
        description: (
          <>
            Em giúp anh hỏi đáp Odoo, sinh form khi cần thông tin, và{" "}
            <strong className="text-foreground">luôn xác nhận trước khi ghi</strong>{" "}
            vào hệ thống.
          </>
        ),
        visual: (
          <div className="flex items-center justify-center py-6">
            <BrandLogo size={88} />
          </div>
        ),
      },
      {
        title: "3 cách bắt đầu nhanh",
        description: "Mỗi tính năng gắn 1 phím tắt — nhớ 1 phím là đủ.",
        visual: <ShortcutsVisual />,
      },
      {
        title: "Em luôn confirm trước khi ghi",
        description: (
          <>
            Khi tạo PR / báo giá / sale order, em sẽ render{" "}
            <strong className="text-foreground">form widget</strong> để anh
            review từng field, có thể sửa, rồi mới ghi vào ERP. Không có
            "auto-submit silently".
          </>
        ),
        visual: <ConfirmVisual />,
      },
    ],
    [],
  );

  if (!open) return null;
  const current = steps[step];
  if (!current) return null;
  const isLast = step === steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={() => dismiss(false)}
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      <div
        className="w-full max-w-md rounded-2xl border bg-popover shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header với gradient mesh */}
        <div className="relative bg-gradient-mesh px-6 pt-5 pb-4 border-b">
          <button
            type="button"
            onClick={() => dismiss(true)}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
            aria-label="Đóng"
          >
            <X className="size-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Bước {step + 1} / {steps.length}
            </span>
            <div className="flex-1 flex items-center gap-1">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    i <= step ? "bg-gradient-brand" : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </div>
          <GradientText
            as="h2"
            id="welcome-title"
            className="text-xl font-bold leading-tight"
          >
            {current.title}
          </GradientText>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4 min-h-[180px]">
          {current.visual}
          <p className="text-sm text-muted-foreground leading-relaxed">
            {current.description}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-1 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => dismiss(true)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Bỏ qua
          </button>
          <div className="flex items-center gap-2">
            {step > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
              >
                ← Lùi
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="brand"
              onClick={() => {
                if (isLast) dismiss(true);
                else setStep((s) => s + 1);
              }}
            >
              {isLast ? "Bắt đầu" : "Tiếp"}
              {!isLast ? <ChevronRight className="size-4 ml-1" /> : null}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShortcutsVisual(): JSX.Element {
  return (
    <div className="space-y-2">
      <ShortcutItem
        Icon={Search}
        kbd="⌘K"
        title="Cmd+K — Tìm hoặc nhảy nhanh"
        hint="Mở palette ở mọi page, tìm form/workflow/setting."
      />
      <ShortcutItem
        Icon={MessageCircle}
        kbd="↩"
        title="Hỏi tự nhiên"
        hint='VD "5 báo giá draft gần nhất", "Tạo PR cho phòng IT".'
      />
      <ShortcutItem
        Icon={Settings}
        kbd="?"
        title="Cmd+/ — Help drawer"
        hint="Mở danh sách shortcut + FAQ bất cứ lúc nào."
      />
    </div>
  );
}

function ShortcutItem({
  Icon,
  kbd,
  title,
  hint,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  kbd: string;
  title: string;
  hint: string;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 rounded-md border bg-background p-2.5">
      <div className="size-8 rounded-md bg-gradient-brand-soft flex items-center justify-center text-primary shrink-0">
        <Icon className="size-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
          <span>{title}</span>
          <kbd className="text-[10px] border rounded px-1.5 py-0.5 bg-muted">
            {kbd}
          </kbd>
        </div>
        <div className="text-[11px] text-muted-foreground">{hint}</div>
      </div>
    </div>
  );
}

function ConfirmVisual(): JSX.Element {
  return (
    <div className="rounded-md border bg-muted/40 p-3 space-y-1.5">
      <div className="text-xs font-semibold flex items-center gap-1.5">
        <span className="size-1.5 rounded-full bg-emerald-500" />
        Form widget — sale_quotation
      </div>
      <div className="text-[11px] text-muted-foreground">
        Em đã chuẩn bị draft báo giá. Anh review rồi bấm "Lưu" để ghi vào Odoo.
      </div>
      <div className="flex gap-1.5 mt-2">
        <span className="text-[10px] border rounded px-2 py-0.5 bg-background">
          Cty Phú An
        </span>
        <span className="text-[10px] border rounded px-2 py-0.5 bg-background">
          Màn hình 27" × 3
        </span>
        <span className="text-[10px] border rounded px-2 py-0.5 bg-background">
          25.5M VND
        </span>
      </div>
    </div>
  );
}
